import React, { useState, useEffect, useRef } from "react";
import ReceiptList from "./ReceiptList";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";

const ViewReceipts = ({ pharmacy_id }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const perPageLimit = 25; // Number of records per page

  // Debounce search query (500ms delay)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Reset pagination when pharmacy_id or debouncedSearchQuery changes
  useEffect(() => {
    setReceipts([]);
    setCurrentPage(1);
    setHasMore(true);
  }, [pharmacy_id, debouncedSearchQuery]);

  // Build query string with pagination and search parameters.
  const query = `?action=getInventoryReceipt&pharmacy_id=${pharmacy_id}&records_number=${perPageLimit}&page=${currentPage}&search=${encodeURIComponent(
    debouncedSearchQuery
  )}`;

  // Fetch receipts using the custom hook.
  // Assumes useItemsFilter returns an error object when API returns a 404.
  const {
    data: newReceipts,
    isLoading,
    error,
  } = useItemsFilter(ApiEndPoint.PHARMACY, query, Boolean(pharmacy_id));

  // Stop infinite scrolling if the API returns a 404.
  useEffect(() => {
    if (error && error.response?.status === 404) {
      console.log("No more data. API returned 404.");
      setHasMore(false);
    }
  }, [error]);

  // Append newly fetched receipts and update hasMore flag.
  useEffect(() => {
    if (newReceipts) {
      if (currentPage === 1) {
        setReceipts(newReceipts);
      } else {
        setReceipts((prev) => [...prev, ...newReceipts]);
      }
      if (newReceipts.length < perPageLimit) {
        setHasMore(false);
      }
    }
  }, [newReceipts, currentPage, perPageLimit]);

  // Infinite scrolling: observe the sentinel element.
  const loadMoreRef = useRef(null);
  useEffect(() => {
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && hasMore) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: "0px", threshold: 1.0 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading]);

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      {/* Header with Title and Search Box */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Receipts</h2>
        <input
          type="text"
          className="px-4 py-2 border border-gray-300 rounded shadow-sm focus:ring-green-300 focus:border-green-400"
          placeholder="Search by supplier name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Render the receipt list */}
      <ReceiptList
        receipts={receipts}
        pharmacy_id={pharmacy_id}
        isLoading={isLoading}
      />

      {/* Sentinel element for infinite scrolling */}
      {hasMore && <div ref={loadMoreRef} className="h-8"></div>}
    </div>
  );
};

export default ViewReceipts;
